import { ChevronRight, MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ROW_LINK_CLASS, useRowLink } from "@/components/row-link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface ResponsiveColumn<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** The column that titles the card below `md` (rendered without a label). */
  primary?: boolean;
  /** Left out of the card layout (e.g. a column that the primary cell already includes). */
  tableOnly?: boolean;
  className?: string;
}

/**
 * One row model, two renderings: a `Table` from `md` up and a card list below `md`. Both are
 * rendered from the same columns and `actions`, so labels and actions cannot drift. The layout
 * switch is CSS only (`hidden md:block` / `md:hidden`), so each rendering keeps its own state.
 * Because both renderings are mounted, `cell` and `actions` MUST NOT render element ids (or
 * `aria-labelledby` references): they would be duplicated.
 */
export function ResponsiveTable<T>({
  rows,
  rowKey,
  columns,
  actions,
  actionsHeader,
  interactive,
}: {
  rows: T[];
  rowKey: (row: T) => string;
  columns: ResponsiveColumn<T>[];
  /** Rendered in the last table cell and at the bottom of each card. */
  actions?: (row: T) => ReactNode;
  actionsHeader?: string;
  /**
   * Whole rows and cards act as one link: rows get a hover state and a trailing chevron, and the
   * primary cell holds the row's one real link, marked `data-row-link`. A click on the rest of
   * the row (or card) is forwarded to it (`row-link.tsx`; no positioned overlay, see there).
   */
  interactive?: boolean;
}) {
  const { t } = useTranslation("common");
  const rowLink = useRowLink();
  const primary = columns.find((column) => column.primary) ?? columns[0];
  const secondary = columns.filter((column) => column !== primary && !column.tableOnly);
  return (
    <>
      <div className="hidden min-w-0 md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id}>{column.header}</TableHead>
              ))}
              {actions ? (
                <TableHead>
                  <span className="sr-only">{actionsHeader}</span>
                </TableHead>
              ) : null}
              {interactive ? (
                <TableHead className="w-8">
                  <span className="sr-only">{t("actions.open")}</span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={rowKey(row)}
                className={cn(interactive && ROW_LINK_CLASS)}
                {...(interactive ? rowLink : undefined)}
              >
                {columns.map((column) => (
                  <TableCell key={column.id} className={column.className}>
                    {column.cell(row)}
                  </TableCell>
                ))}
                {actions ? (
                  <TableCell>
                    <div className="flex justify-end gap-2">{actions(row)}</div>
                  </TableCell>
                ) : null}
                {interactive ? (
                  <TableCell className="w-8 text-right">
                    <ChevronRight className="text-muted-foreground ml-auto size-4" aria-hidden />
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ul className="grid gap-3 md:hidden">
        {rows.map((row) => {
          const cardActions = actions?.(row);
          return (
            <li
              key={rowKey(row)}
              className={cn(
                "grid min-w-0 gap-3 rounded-lg border bg-card p-4",
                interactive && cn(ROW_LINK_CLASS, "hover:bg-muted/50 relative transition-colors"),
              )}
              {...(interactive ? rowLink : undefined)}
            >
              {interactive ? (
                <ChevronRight
                  className="text-muted-foreground absolute top-4 right-3 size-4"
                  aria-hidden
                />
              ) : null}
              {primary ? (
                <div className={cn("min-w-0 break-words", interactive && "pr-6")}>
                  {primary.cell(row)}
                </div>
              ) : null}
              <dl className="grid gap-2 text-sm">
                {secondary.map((column) => (
                  <div
                    key={column.id}
                    className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1"
                  >
                    <dt className="text-muted-foreground">{column.header}</dt>
                    <dd className={cn("min-w-0 break-words", column.className)}>
                      {column.cell(row)}
                    </dd>
                  </div>
                ))}
              </dl>
              {cardActions ? (
                <div className="flex flex-wrap justify-end gap-2">{cardActions}</div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** Icon button that opens a menu of row actions; its accessible name includes the row name. */
export function RowActionsMenu({ name, children }: { name: string; children: ReactNode }) {
  const { t } = useTranslation("common");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon-sm" aria-label={t("actions.rowActions", { name })}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}
